import { InfoOutlined } from "@mui/icons-material";
import { Box, Grid2, IconButton, Popover, Typography } from "@mui/material";
import { GridSize, SxProps } from "@mui/system";
import { type PropsWithChildren, type ReactNode, useState } from "react";

import { Markdown } from "../../molecules/markdown/markdown";

export type FormFieldWrapperProps = {
  description?: string;
  infoSlot?: ReactNode;
  label?: string | ReactNode;
  fieldWrapperSx?: SxProps;

  uiOptions?: {
    fieldSize?: Record<string, GridSize>;
    skinnyLabel?: boolean;
    centerItems?: boolean;
    narrowInfoSlotIcon?: boolean;
    /** Makes this field fill the height of the tallest field in its grid row. */
    equalRowHeight?: boolean;
    /** Renders the field pinned to the bottom of the tab, below the regular grid. */
    pinnedToBottom?: boolean;
    /** Keeps a decorative field (header, hint, …) out of the tab completion status. */
    excludeFromCompletion?: boolean;
  };
};

export const FormFieldWrapper: React.FC<
  PropsWithChildren<FormFieldWrapperProps>
> = ({
  label,
  description,
  infoSlot,
  uiOptions: {
    fieldSize,
    skinnyLabel,
    centerItems,
    narrowInfoSlotIcon,
    equalRowHeight,
  } = {},
  children,
  fieldWrapperSx = {},
}) => {
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLButtonElement | null>(
    null
  );

  return (
    <Grid2
      size={fieldSize ?? { xs: 12 }}
      sx={
        equalRowHeight
          ? { display: "flex", flexDirection: "column" }
          : undefined
      }
    >
      {label && typeof label === "string" ? (
        <Typography
          sx={{ fontWeight: !skinnyLabel ? 500 : undefined, fontSize: "14px" }}
          gutterBottom
        >
          {label}
        </Typography>
      ) : (
        label
      )}
      {description && <Markdown>{description}</Markdown>}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: centerItems ? "center" : "inherit",
          ...(equalRowHeight ? { flex: "1 1 auto", minHeight: 0 } : {}),
        }}
      >
        <Box
          sx={{
            flex: "1 1 100%",
            position: "relative",
            ...(equalRowHeight
              ? {
                  height: "100%",
                  minHeight: 0,
                  "& > .MuiFormControl-root, & > .MuiInputBase-root": {
                    height: "100%",
                  },
                }
              : {}),
            ...fieldWrapperSx,
          }}
        >
          {children}
        </Box>
        {infoSlot && (
          <Box
            sx={{
              flex: "0 0 50px",
              display:
                infoSlot || !fieldSize || fieldSize?.xs === 12
                  ? "flex"
                  : "none",
              alignItems: "flex-start",
              justifyContent: narrowInfoSlotIcon ? "flex-start" : "flex-end",
              paddingTop: 0.25,
            }}
          >
            <>
              <IconButton
                onClick={(e) => setPopoverAnchor(e.target as HTMLButtonElement)}
              >
                <InfoOutlined />
              </IconButton>
              <Popover
                open={!!popoverAnchor}
                anchorEl={popoverAnchor}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                onClose={() => setPopoverAnchor(null)}
              >
                {infoSlot}
              </Popover>
            </>
          </Box>
        )}
      </Box>
    </Grid2>
  );
};
